import React, { useEffect, useState } from 'react';
import hash from 'object-hash';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { Box, Card, FlexBox, MuiBox, MuiTable, SvgIcon, Typography, color } from '@kis-technologies/kis-ui';
import MediaTableRow from './MediaTableRow';
import { useStyles } from '../styled';
import { MediaResponse, MediaTableProps, MediaTypes } from '../types';
import { getMediaSource, getMediaTypeLabel } from '../utils';

const MediaTable = (props: MediaTableProps) => {
  const {
    mediaRecords,
    mediaType,
    onDeleteClick,
    onDownloadClick,
    onEditClick,
    onPreviewClick,
    onOrderChange,
    promoCodes
  } = props;

  const classes = useStyles({ genreTab: false })();

  const mediaTypeLabel = getMediaTypeLabel(mediaType);

  // ----- DRAG AND DROP FUNCTIONALITY (SLIDESHOW ONLY) -----
  const sensors = useSensors(useSensor(PointerSensor));
  const [localImages, setLocalImages] = useState<MediaResponse[]>(mediaRecords || []);
  const [sortColumn, setSortColumn] = useState<string>('label');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [dimensionsMap, setDimensionsMap] = useState<Record<string, { width: number; height: number }>>({});

  useEffect(() => {
    setLocalImages(mediaRecords || []);
  }, [mediaRecords]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDragEnd = (event: DragEndEvent) => {
    if (!onOrderChange) return;

    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = localImages.findIndex((img) => img.s3Name === active.id);
      const newIndex = localImages.findIndex((img) => img.s3Name === over.id);
      const reordered = arrayMove(localImages, oldIndex, newIndex);
      setLocalImages(reordered);
      onOrderChange(reordered.map((i) => i.s3Name));
    }
  };

  const loadDimensions = (mediaSource: string, id: string) => {
    if (!mediaSource) return;

    const img = new Image();
    img.src = mediaSource;

    img.onload = () => {
      setDimensionsMap((prev) => ({
        ...prev,
        [id]: { width: img.width, height: img.height }
      }));
    };
  };

  const getSortedPromoData = () => {
    if (mediaType !== MediaTypes.PROMO_CODE_IMAGE || !promoCodes) return [];

    const mapped = promoCodes.map((code) => {
      const mediaData = mediaRecords.find((r) => r.name === hash(code));
      const id = mediaData?.s3Name || code;

      const mediaSource = mediaData ? getMediaSource(mediaType, mediaData) : '';

      if (mediaSource && !dimensionsMap[id]) {
        loadDimensions(mediaSource, id);
      }

      return { promoCode: code, mediaData, id };
    });

    return mapped.sort((a, b) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let valA: any;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let valB: any;

      if (sortColumn === 'label') {
        const aVal = a.promoCode ?? '';
        const bVal = b.promoCode ?? '';

        const isNumberA = !isNaN(Number(aVal));
        const isNumberB = !isNaN(Number(bVal));

        if (isNumberA && isNumberB) {
          return sortDirection === 'asc' ? Number(aVal) - Number(bVal) : Number(bVal) - Number(aVal);
        }

        return sortDirection === 'asc'
          ? String(aVal).localeCompare(String(bVal))
          : String(bVal).localeCompare(String(aVal));
      }

      if (sortColumn === 'fileName') {
        valA = a.mediaData?.name || '';
        valB = b.mediaData?.name || '';
        return sortDirection === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      }

      if (sortColumn === 'size') {
        const dimA = dimensionsMap[a.id];
        const dimB = dimensionsMap[b.id];

        const areaA = dimA ? dimA.width * dimA.height : 0;
        const areaB = dimB ? dimB.width * dimB.height : 0;

        return sortDirection === 'asc' ? areaA - areaB : areaB - areaA;
      }

      return 0;
    });
  };
  const handleSort = (column: string) => {
    let direction: 'asc' | 'desc' = 'asc';

    if (sortColumn === column) {
      direction = sortDirection === 'asc' ? 'desc' : 'asc';
    }

    setSortColumn(column);
    setSortDirection(direction);
  };

  const SortArrow = () => {
    return (
      <Box sx={{ display: 'inline-block', cursor: 'pointer', backgroundColor: 'inherit' }}>
        <SvgIcon name={sortDirection === 'asc' ? 'drop_up' : 'drop_down'} size={15} style={{ marginLeft: '5px' }} />
      </Box>
    );
  };

  // --------------------------------------------------------

  const renderTableRow = (idx: number, mediaRecord?: MediaResponse, promoCode?: string) => {
    const mediaSource = mediaRecord ? getMediaSource(mediaType, mediaRecord) : null;
    const tableRowId = mediaRecord?.s3Name || promoCode || idx.toString();
    const label = mediaType === MediaTypes.ATTRACT_IMAGE ? mediaRecord?.name : promoCode;

    return (
      <MediaTableRow
        key={tableRowId}
        enableDragAndDrop={!!onOrderChange} // SLIDESHOW ONLY
        id={tableRowId}
        mediaSource={mediaSource || undefined}
        label={label} // PROMO CODES AND SLIDESHOW ONLY
        mediaRecord={mediaRecord}
        mediaTypeLabel={mediaTypeLabel}
        onDeleteClick={onDeleteClick && mediaRecord ? () => onDeleteClick(mediaRecord) : undefined}
        onDownloadClick={onDownloadClick && mediaRecord ? () => onDownloadClick(mediaRecord) : undefined}
        onEditClick={onEditClick ? () => onEditClick(mediaRecord, promoCode) : undefined}
        onPreviewClick={onPreviewClick && mediaSource ? () => onPreviewClick(mediaSource) : undefined}
      />
    );
  };

  return (
    <FlexBox fullWidth noWrap>
      <Box>
        <MuiBox className={classes.tableDetailsOuterBox}>
          <Card hasBorderRadius raised fullHeight>
            <Box flexOverride="1">
              <MuiTable.TableContainer
                className={
                  mediaType === MediaTypes.PROMO_CODE_IMAGE || mediaType === MediaTypes.ATTRACT_IMAGE
                    ? classes.tableContainer
                    : classes.singleRowTableContainer
                }
              >
                <MuiTable.Table stickyHeader>
                  <MuiTable.TableHead>
                    <MuiTable.TableRow className={classes.tableRow}>
                      {/* LABEL (FOR PROMO CODES AND SLIDESHOW ONLY) */}
                      {(mediaType === MediaTypes.PROMO_CODE_IMAGE || mediaType === MediaTypes.ATTRACT_IMAGE) && (
                        <MuiTable.TableCell
                          className={`${classes.tableCell} ${classes.tableHeadCell} ${classes.tableLabelCell}`}
                          onClick={() => mediaType === MediaTypes.PROMO_CODE_IMAGE && handleSort('label')}
                          sx={{ cursor: mediaType === MediaTypes.PROMO_CODE_IMAGE ? 'pointer' : 'default' }}
                        >
                          {mediaType === MediaTypes.ATTRACT_IMAGE ? 'Slide Name' : 'Promo Code'}
                          {mediaType === MediaTypes.PROMO_CODE_IMAGE && sortColumn === 'label' && <SortArrow />}
                        </MuiTable.TableCell>
                      )}
                      {/* FILE NAME */}
                      <MuiTable.TableCell
                        className={`${classes.tableCell} ${classes.tableHeadCell} ${classes.tableFileNameCell}`}
                        onClick={() => mediaType === MediaTypes.PROMO_CODE_IMAGE && handleSort('fileName')}
                        sx={{ cursor: mediaType === MediaTypes.PROMO_CODE_IMAGE ? 'pointer' : 'default' }}
                      >
                        File Name
                        {mediaType === MediaTypes.PROMO_CODE_IMAGE && sortColumn === 'fileName' && <SortArrow />}
                      </MuiTable.TableCell>
                      {/* DIMENSIONS */}
                      <MuiTable.TableCell
                        className={`${classes.tableCell} ${classes.tableHeadCell} ${classes.tableDimensionsCell}`}
                        onClick={() => mediaType === MediaTypes.PROMO_CODE_IMAGE && handleSort('size')}
                        sx={{ cursor: mediaType === MediaTypes.PROMO_CODE_IMAGE ? 'pointer' : 'default' }}
                      >
                        Size
                        {mediaType === MediaTypes.PROMO_CODE_IMAGE && sortColumn === 'size' && <SortArrow />}
                      </MuiTable.TableCell>
                      {/* ACTIONS */}
                      <MuiTable.TableCell
                        className={`${classes.tableCell} ${classes.tableHeadCell} ${classes.tableActionsCell}`}
                      >
                        Actions
                      </MuiTable.TableCell>
                    </MuiTable.TableRow>
                  </MuiTable.TableHead>
                  {mediaType === MediaTypes.ATTRACT_IMAGE ? (
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                      <SortableContext
                        items={localImages.map((mediaRecord) => mediaRecord.s3Name)}
                        strategy={verticalListSortingStrategy}
                      >
                        <MuiTable.TableBody>
                          {localImages.map((mediaRecord, idx) => renderTableRow(idx, mediaRecord))}
                        </MuiTable.TableBody>
                      </SortableContext>
                    </DndContext>
                  ) : (
                    <MuiTable.TableBody>
                      {promoCodes
                        ? getSortedPromoData().map((item, idx) => renderTableRow(idx, item.mediaData, item.promoCode))
                        : mediaRecords.map((mediaRecord, idx) => renderTableRow(idx, mediaRecord))}
                    </MuiTable.TableBody>
                  )}
                </MuiTable.Table>
              </MuiTable.TableContainer>
            </Box>
          </Card>
        </MuiBox>

        {/* DISCLAIMER (SLIDESHOW ONLY) */}
        {mediaType === MediaTypes.ATTRACT_IMAGE && mediaRecords.length >= 10 && (
          <Box margin="4 0">
            <Typography color={color.RED}>You can have up to 10 images in your slideshow.</Typography>
          </Box>
        )}
      </Box>
    </FlexBox>
  );
};

export default MediaTable;
